import React from 'react';
import { Link } from 'react-router-dom';
import { Fuel, ChefHat, Users, MapPin, ArrowRight, Star, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const features = [
    {
      icon: <Fuel size={36} />,
      title: 'Marathon Fuel',
      description: 'Premium Marathon brand gasoline with top-tier detergent additives for peak performance.'
    },
    {
      icon: <ChefHat size={36} />,
      title: 'Fresh Daily',
      description: 'Hand-breaded Krispy Krunchy Chicken prepared fresh throughout the day, never frozen.'
    },
    {
      icon: <Users size={36} />,
      title: 'Family Meals',
      description: 'Generous family meal packages perfect for gatherings, game nights, and quick dinners.'
    },
    {
      icon: <MapPin size={36} />,
      title: 'Hope, Indiana',
      description: 'Conveniently located on Main Street. Your trusted neighborhood convenience stop.'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years Serving Hope' },
    { number: '7 Days', label: 'Open Weekly' },
    { number: '100%', label: 'Fresh Chicken' }
  ];

  return (
    <div>
      {/* Hero Section with Enhanced Design */}
      <section 
        className="hero-section"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1567777176186-dfa735f1fe20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxnYXMlMjBzdGF0aW9ufGVufDB8fHx8MTc3MjQxNTMxOHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-flex items-center px-5 py-3 rounded-full mb-6" 
               style={{ 
                 background: 'rgba(255, 255, 255, 0.95)',
                 boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
               }}>
            <Star size={18} className="mr-2" style={{ color: 'var(--marathon-yellow)' }} />
            <span className="text-sm font-bold" style={{ color: 'var(--marathon-blue)' }}>
              Hope, Indiana's Trusted Stop Since 2009
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight" style={{ color: 'var(--white)' }}>
            Fuel Up. Eat Better.<br />
            <span style={{ 
              background: 'linear-gradient(135deg, var(--marathon-yellow) 0%, var(--white) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              All in One Stop.
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto font-medium" style={{ color: 'var(--white)' }}>
            Hope Tobacco
          </p>
          <p className="text-lg md:text-xl mb-10 max-w-3xl mx-auto" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Featuring Marathon Gas & Krispy Krunchy Chicken
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/menu">
              <Button 
                size="lg" 
                className="text-lg px-10 py-7 font-bold shadow-2xl"
                style={{ 
                  background: 'linear-gradient(135deg, var(--marathon-red) 0%, var(--marathon-red-light) 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                View Menu <ArrowRight className="ml-2" size={22} />
              </Button>
            </Link>
            <Link to="/location">
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-10 py-7 font-bold border-3 glass-card"
                style={{ 
                  borderColor: 'var(--white)',
                  borderWidth: '2px',
                  color: 'var(--white)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Get Directions
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mt-16">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="p-5 rounded-xl" 
                style={{ 
                  background: 'rgba(255, 255, 255, 0.95)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                  backdropFilter: 'blur(20px)'
                }}
              >
                <div 
                  className="text-4xl md:text-5xl font-black mb-2"
                  style={{ 
                    background: 'linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-red) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {stat.number}
                </div>
                <div className="text-xs md:text-sm font-semibold" style={{ color: 'var(--gray-700)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced Professional Design */}
      <section className="section section-patriotic">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="premium-badge mb-6">Why Choose Hope Tobacco?</div>
            <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ color: 'var(--marathon-blue)' }}>
              Your Neighborhood One-Stop Shop
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg md:text-xl max-w-3xl mx-auto mt-6" style={{ color: 'var(--gray-700)' }}>
              We're more than just a gas station. We're your trusted local destination for quality Marathon fuel, 
              fresh Krispy Krunchy Chicken, and friendly service.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="professional-card text-center"
              >
                <div 
                  className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center"
                  style={{ 
                    background: 'linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-red) 100%)',
                    color: 'var(--white)'
                  }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--gray-900)' }}>
                  {feature.title}
                </h3>
                <p className="leading-relaxed" style={{ color: 'var(--gray-600)' }}>
                  {feature.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Krispy Krunchy Chicken Section - Premium Layout */}
      <section className="section section-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="premium-badge-red mb-6">Featured Brand</div>
              <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ color: 'var(--marathon-blue)' }}>
                Krispy Krunchy<br />Chicken
              </h2>
              <div className="section-divider-left"></div>
              <p className="text-lg mb-6 leading-relaxed" style={{ color: 'var(--gray-700)' }}>
                Experience the bold, authentic flavors of Cajun-inspired fried chicken made fresh daily. 
                Our hand-breaded chicken, tenders, wings, and sides are prepared with care to give you 
                that perfect crispy crunch every time.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  { icon: <Award size={20} />, text: 'Hand-breaded chicken & tenders' },
                  { icon: <Star size={20} />, text: 'Fresh, never frozen' },
                  { icon: <Users size={20} />, text: 'Family meal deals available' },
                  { icon: <Clock size={20} />, text: 'Made to order throughout the day' }
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-4">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ 
                        background: 'linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-red) 100%)',
                        color: 'white'
                      }}
                    >
                      {item.icon}
                    </div>
                    <span className="text-base font-medium" style={{ color: 'var(--gray-800)' }}>{item.text}</span>
                  </li>
                ))}
              </ul>
              <Link to="/menu">
                <Button 
                  size="lg"
                  className="font-bold text-base px-8 py-6"
                  style={{ 
                    background: 'linear-gradient(135deg, var(--marathon-blue) 0%, var(--marathon-red) 100%)',
                    color: 'white'
                  }}
                >
                  Explore Full Menu <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                'https://images.unsplash.com/photo-1694853651800-3e9b4aa96a42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxmcmllZCUyMGNoaWNrZW58ZW58MHx8fHwxNzcyNDE2MDYxfDA&ixlib=rb-4.1.0&q=85',
                'https://images.unsplash.com/photo-1650939986300-ce9609921fa7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxjaGlja2VuJTIwd2luZ3N8ZW58MHx8fHwxNzcyNDE2MDY3fDA&ixlib=rb-4.1.0&q=85',
                'https://images.unsplash.com/photo-1567620832903-9fc6debc209f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxjaGlja2VuJTIwd2luZ3N8ZW58MHx8fHwxNzcyNDE2MDY3fDA&ixlib=rb-4.1.0&q=85',
                'https://images.unsplash.com/photo-1569058242253-92a9c755a0ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxmcmllZCUyMGNoaWNrZW58ZW58MHx8fHwxNzcyNDE2MDYxfDA&ixlib=rb-4.1.0&q=85'
              ].map((src, index) => (
                <img 
                  key={index}
                  src={src}
                  alt={`Krispy Krunchy Chicken ${index + 1}`}
                  className={`professional-image w-full h-56 object-cover ${index % 2 === 1 ? 'mt-8' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with Blue-White-Red Theme */}
      <section className="section section-blue">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">Visit Us Today</h2>
          <div className="section-divider" style={{ background: 'linear-gradient(90deg, var(--white) 0%, var(--marathon-red) 50%, var(--white) 100%)' }}></div>
          <p className="text-xl md:text-2xl mb-10 leading-relaxed" style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
            Stop by Hope Tobacco for quality Marathon fuel and fresh Krispy Krunchy Chicken.<br />
            <span className="font-semibold">704 Main Street, Hope, Indiana</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8125465780">
              <Button 
                size="lg"
                className="text-lg px-10 py-7 font-bold"
                style={{ 
                  background: 'linear-gradient(135deg, var(--marathon-red) 0%, var(--marathon-red-light) 100%)',
                  color: 'white'
                }}
              >
                Call Now: (812) 546-5780
              </Button>
            </a>
            <Link to="/location">
              <Button 
                size="lg"
                variant="outline"
                className="text-lg px-10 py-7 font-bold border-3"
                style={{ 
                  borderColor: 'var(--white)',
                  borderWidth: '2px',
                  color: 'var(--white)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Get Directions
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
